import { vtkSharedVolumeMapper } from '../vtkClasses/index.js';
import { getConfiguration, getLossyDownsampling } from '../../init.js';
import vtkVolumeMapper from '@kitware/vtk.js/Rendering/Core/VolumeMapper.js';
import vtkDataArray from '@kitware/vtk.js/Common/Core/DataArray.js';
import vtkBoundingBox from '@kitware/vtk.js/Common/DataModel/BoundingBox.js';
export const MAXIMUM_SAMPLES_PER_RAY = 4000;
export function sampleDistanceOf(imageData, texture, multiplier = getConfiguration().rendering?.volumeRendering
    ?.sampleDistanceMultiplier || 1) {
    const grid = texture?.getGrid?.();
    const spacing = grid?.spacing ?? imageData.getSpacing();
    const distance = (multiplier * (spacing[0] + spacing[1] + spacing[2])) / 6;
    if (!getLossyDownsampling().final) {
        return distance;
    }
    const diagonal = vtkBoundingBox.getDiagonalLength(imageData.getBounds());
    return Math.max(distance, diagonal / (MAXIMUM_SAMPLES_PER_RAY - 1));
}
const idleSampleDistanceMultipliers = new WeakMap();
export function setIdleSampleDistanceMultiplier(mapper, multiplier) {
    idleSampleDistanceMultipliers.set(mapper, multiplier);
}
export function idleSampleDistanceMultiplierOf(mapper) {
    return (idleSampleDistanceMultipliers.get(mapper) ??
        (getConfiguration().rendering?.volumeRendering?.sampleDistanceMultiplier ||
            1));
}
export function sampleDistanceRatiosOf(mapper, texture = mapper?.getScalarTexture?.()) {
    const current = mapper?.getSampleDistance?.();
    const imageData = mapper?.getInputData?.();
    if (!imageData || !(current > 0)) {
        return { current: 1, idle: 1 };
    }
    const baseline = sampleDistanceOf(imageData, texture, 1);
    if (!(baseline > 0)) {
        return { current: 1, idle: 1 };
    }
    const idle = sampleDistanceOf(imageData, texture, idleSampleDistanceMultiplierOf(mapper));
    return { current: current / baseline, idle: idle / baseline };
}
export default function createVolumeMapper(imageData, vtkOpenGLTexture) {
    const volumeMapper = vtkSharedVolumeMapper.newInstance();
    volumeMapper.setInputData(imageData);
    volumeMapper.setMaximumSamplesPerRay(MAXIMUM_SAMPLES_PER_RAY);
    volumeMapper.setSampleDistance(sampleDistanceOf(imageData, vtkOpenGLTexture));
    if (vtkOpenGLTexture) {
        volumeMapper.setScalarTexture(vtkOpenGLTexture);
    }
    return volumeMapper;
}
export function convertMapperToNotSharedMapper(sharedMapper) {
    const volumeMapper = vtkVolumeMapper.newInstance();
    volumeMapper.setBlendMode(sharedMapper.getBlendMode());
    const imageData = sharedMapper.getInputData();
    const { voxelManager } = imageData.get('voxelManager');
    const values = voxelManager.getCompleteScalarDataArray();
    const scalarArray = vtkDataArray.newInstance({
        name: `Pixels`,
        values,
    });
    imageData.getPointData().setScalars(scalarArray);
    volumeMapper.setInputData(imageData);
    volumeMapper.setMaximumSamplesPerRay(sharedMapper.getMaximumSamplesPerRay());
    volumeMapper.setSampleDistance(sharedMapper.getSampleDistance());
    return volumeMapper;
}
